import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Building, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, Waves, Square } from 'lucide-react';

const StrataApartmentCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "What areas of a strata building do you clean?",
      a: "Common areas including tiled and concrete surfaces, carparks, stairwells and windows. High-pressure cleaning, rotary tile and grout scrubbing and extraction, carpet cleaning, and general cleaning including cobwebs and rubbish removal are all part of the work, whatever the building needs."
    },
    {
      q: "Do you offer ongoing scheduled cleaning, or just one-off jobs?",
      a: "Both. Some buildings suit a regular scheduled maintenance plan, others prefer to call as issues come up. We can work either way, depending on what suits your strata or facilities manager."
    },
    {
      q: "How do you communicate with our strata or facilities manager?",
      a: "Clear, direct communication is something we prioritise specifically, so requests, schedules and any issues are actioned properly rather than getting lost between the building manager and the cleaning contractor."
    },
    {
      q: "Do you clean carparks and stairwells?",
      a: "Yes. Stairwell cleaning covers rotary tile and grout scrubbing and extraction, carpet cleaning, and general cleaning including cobwebs and rubbish removal, along with carpark washdowns, all part of our standard strata and apartment complex work."
    }
  ];

  // Service Schema for SEO - Perth metro only with focus on higher-density areas
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Strata & Apartment Complex Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "East Perth", "South Perth", "Como", "Applecross", "Fremantle", 
      "Subiaco", "Leederville", "Canning Vale", "Bibra Lake", "Jandakot", 
      "Balcatta", "Rockingham", "Joondalup", "Yanchep", "Mandurah"
    ],
    "description": "Strata and apartment complex cleaning across Perth. High-pressure cleaning, carpark washdowns, window cleaning, stairwell cleaning, and scheduled maintenance with clear communication for strata and facilities managers."
  };

  const relatedServices = [
    {
      title: 'Commercial High-Pressure Cleaning',
      description: 'Tile, concrete and carpark washdowns.',
      link: '/services/commercial-high-pressure-cleaning-perth',
      icon: Waves
    },
    {
      title: 'Commercial Window Cleaning',
      description: 'Window cleaning for apartment and strata buildings, including multi-storey access.',
      link: '/services/commercial-window-cleaning-perth',
      icon: Square
    }
  ];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/industries/strata-apartment-cleaning-perth" />
        <title>Strata & Apartment Complex Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Strata and apartment complex cleaning across Perth. High-pressure cleaning, carpark washdowns, stairwells, windows. Clear communication with strata and facilities managers." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="strata-apartment-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.pexels.com/photos/31711386/pexels-photo-31711386.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Perth skyline with apartments along Swan River"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Building size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Strata & Apartments</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Strata & Apartment Complex Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Reliable communication with strata and facilities managers, so nothing gets missed or miscommunicated
              </p>
              <p className="text-gray-400 mb-8">
                Strata and apartment complexes need a cleaning partner who's easy to deal with as much as one who does the work properly, clear communication with strata and facilities management so requests, schedules and issues never get lost in the gaps. Extreme Clean Australia handles both scheduled maintenance and ad-hoc call-outs for strata and apartment buildings across Perth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                High-pressure cleaning of tiles, concrete and grout, carpark area washdowns, window cleaning, and full stairwell cleaning, rotary tile and grout scrubbing and extraction, carpet cleaning, and general cleaning including cobwebs and rubbish removal. If a strata or apartment complex needs it cleaned, we can handle it. Scope is built around each building's common areas and what the strata or facilities manager actually needs.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'High-pressure tile and concrete cleaning',
                  'Carpark area washdowns',
                  'Window cleaning',
                  'Stairwell cleaning',
                  'Rotary tile and grout scrubbing',
                  'Carpet cleaning',
                  'Cobweb removal',
                  'Rubbish removal'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Built on Clear Communication */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Communication</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Built on Clear Communication</h2>
              <p className="text-gray-600">
                A lot of what goes wrong with strata cleaning isn't the cleaning itself, it's poor communication between the contractor and the people managing the building. We prioritise staying in close contact with strata and facilities managers, so requests are actioned, schedules are clear, and nothing falls through the cracks.
              </p>
            </div>
          </div>
        </section>

        {/* Scheduled Maintenance and Ad-Hoc Call-Outs */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Flexibility</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Scheduled Maintenance and Ad-Hoc Call-Outs</h2>
              <p className="text-gray-600">
                In our experience, strata and apartment cleaning work tends to be a mix, some buildings run on a regular scheduled maintenance plan, while others call as issues come up, a washdown after an incident, a one-off deep clean, cobwebs in a stairwell. We handle both, and can work with a building however it actually operates, rather than forcing a fixed schedule that doesn't suit.
              </p>
            </div>
          </div>
        </section>

        {/* Growing Our Strata Presence */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Growth</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Growing Our Strata Presence Across Perth</h2>
              <p className="text-gray-300">
                Strata and apartment building cleaning is a growing focus for us, and we're actively building our presence in this space, particularly in Perth's higher-density pockets, East Perth, South Perth, Como, Applecross, Subiaco and Leederville among them, where apartment living and strata-managed buildings are most common.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              <p className="text-gray-600">
                We provide strata and apartment complex cleaning across the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Canning Vale, Bibra Lake, Jandakot, Balcatta, Rockingham and Joondalup, with a particular focus on higher-density areas including Perth, East Perth, South Perth, Como, Applecross, Fremantle, Subiaco and Leederville.
              </p>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Strata Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for strata and apartment complex cleaning across Perth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default StrataApartmentCleaning;
